import { create } from 'zustand'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// ============================================
// BITS PRODUCT CATALOG (Fixed Pricing)
// ============================================

// Tier 1 — Cosmetic Unlocks
export const COSMETIC_PRODUCTS = [
  { id: 'golden_pot', name: 'Golden Pot', type: 'plant_variant', bits: 50, description: 'A shiny gold pot for your plant', rarity: 'rare' },
  { id: 'plant_neon', name: 'Neon Plant', type: 'plant_variant', bits: 100, description: 'Glowing neon skin for your plant', rarity: 'rare' },
  { id: 'plant_crystal', name: 'Crystal Plant', type: 'plant_variant', bits: 100, description: 'Sparkling crystal skin for your plant', rarity: 'rare' },
  { id: 'plant_galaxy', name: 'Galaxy Plant', type: 'plant_variant', bits: 100, description: 'Cosmic galaxy skin for your plant', rarity: 'rare' },
  { id: 'insect_golden', name: 'Golden Insect', type: 'insect_variant', bits: 100, description: 'Golden skin for your insect', rarity: 'rare' },
  { id: 'insect_rainbow', name: 'Rainbow Insect', type: 'insect_variant', bits: 100, description: 'Rainbow skin for your insect', rarity: 'rare' },
  { id: 'insect_shadow', name: 'Shadow Insect', type: 'insect_variant', bits: 100, description: 'Dark shadow skin for your insect', rarity: 'rare' },
  { id: 'name_color', name: 'Custom Name Color', type: 'name_color', bits: 250, description: 'Colored username above your entity', rarity: 'epic' },
  { id: 'plant_cherry_blossom', name: 'Cherry Blossom', type: 'legendary_plant', bits: 500, description: 'Exclusive cherry blossom tree', rarity: 'legendary' },
  { id: 'plant_crystal_tree', name: 'Crystal Tree', type: 'legendary_plant', bits: 500, description: 'Exclusive crystal tree', rarity: 'legendary' },
  { id: 'plant_venus_flytrap', name: 'Venus Flytrap', type: 'legendary_plant', bits: 500, description: 'Exclusive venus flytrap', rarity: 'legendary' },
  { id: 'insect_moth', name: 'Moth', type: 'legendary_insect', bits: 500, description: 'Exclusive moth companion', rarity: 'legendary' },
  { id: 'insect_mantis', name: 'Praying Mantis', type: 'legendary_insect', bits: 500, description: 'Exclusive praying mantis', rarity: 'legendary' },
  { id: 'insect_scorpion', name: 'Scorpion', type: 'legendary_insect', bits: 500, description: 'Exclusive scorpion companion', rarity: 'legendary' }
]

// Tier 2 — Instant Effects (one-time, no unlock needed)
export const EFFECT_PRODUCTS = [
  { id: 'rain', name: 'Rain', bits: 1, description: 'Waters ALL plants for 30s', icon: '🌧️' },
  { id: 'fireworks', name: 'Fireworks', bits: 50, description: 'Fireworks over the terrarium', icon: '🎆' },
  { id: 'mega_bloom', name: 'Mega Bloom', bits: 100, description: 'Instantly blooms your plant', icon: '🌸' },
  { id: 'swarm', name: 'Butterfly Swarm', bits: 200, description: 'Spawns 10 temporary butterflies', icon: '🦋' },
  { id: 'earthquake', name: 'Earthquake', bits: 500, description: 'Shakes the whole terrarium', icon: '🌍' }
]

// Tier 3 — Persistent Perks (cumulative bits threshold)
export const PERK_THRESHOLDS = [
  { id: 'second_plant', name: 'Second Plant Slot', threshold: 500, description: 'Grow 2 plants at once', icon: '🌱' },
  { id: 'second_insect', name: 'Second Insect Slot', threshold: 1000, description: 'Have 2 insects at once', icon: '🐛' },
  { id: 'vip_badge', name: 'VIP Badge', threshold: 2500, description: 'Star icon next to your name', icon: '⭐' },
  { id: 'custom_name', name: 'Custom Plant Name', threshold: 5000, description: 'Name your plant anything', icon: '✏️' }
]

// ============================================
// BITS STORE
// ============================================

const useBitsStore = create((set, get) => ({
  // Per-user bits data (keyed by userId)
  userBits: {},
  // Per-user cosmetic unlocks (keyed by userId)
  userCosmetics: {},
  // Active effects on the terrarium
  activeEffects: [],
  // Notification queue for bits events
  bitsNotifications: [],
  broadcasterLogin: null,

  setBroadcasterLogin: (login) => set({ broadcasterLogin: login }),

  // Record a cheer and update local state
  recordCheer: async (userId, username, bitsAmount) => {
    const { broadcasterLogin } = get()
    if (!broadcasterLogin) return

    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/broadcaster/${broadcasterLogin}/bits/cheer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, username, bitsAmount })
      })
      const data = await response.json()

      set(state => ({
        userBits: {
          ...state.userBits,
          [userId]: { totalBits: data.totalBits, username }
        }
      }))

      return data.totalBits
    } catch (error) {
      console.error('Failed to record cheer:', error)
      return null
    }
  },

  // Get user bits (from cache or API)
  getUserBits: async (userId) => {
    const { userBits, broadcasterLogin } = get()
    if (userBits[userId]) return userBits[userId].totalBits

    if (!broadcasterLogin) return 0

    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/broadcaster/${broadcasterLogin}/bits/user/${userId}`)
      const data = await response.json()

      set(state => ({
        userBits: {
          ...state.userBits,
          [userId]: { totalBits: data.totalBits || 0 }
        }
      }))

      return data.totalBits || 0
    } catch {
      return 0
    }
  },

  // Get user cosmetics (from cache or API)
  getUserCosmetics: async (userId, forceRefresh = false) => {
    const { userCosmetics, broadcasterLogin } = get()
    if (!forceRefresh && userCosmetics[userId]) return userCosmetics[userId]

    if (!broadcasterLogin) return []

    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/broadcaster/${broadcasterLogin}/bits/cosmetics/${userId}`)
      const data = await response.json()

      set(state => ({
        userCosmetics: {
          ...state.userCosmetics,
          [userId]: data
        }
      }))

      return data
    } catch {
      return []
    }
  },

  // Check if user has unlocked a perk based on cumulative bits
  hasReachedPerk: (userId, perkId) => {
    const { userBits } = get()
    const perk = PERK_THRESHOLDS.find(p => p.id === perkId)
    if (!perk) return false
    return (userBits[userId]?.totalBits || 0) >= perk.threshold
  },

  // Check if user has a specific cosmetic
  hasCosmetic: (userId, cosmeticId) => {
    const { userCosmetics } = get()
    const cosmetics = userCosmetics[userId] || []
    return cosmetics.some(c => c.cosmeticId === cosmeticId)
  },

  // Add a cosmetic to local cache (called when SSE redemption arrives)
  addUserCosmetic: (userId, cosmeticId) => {
    set(state => {
      const existing = state.userCosmetics[userId] || []
      if (existing.some(c => c.cosmeticId === cosmeticId)) return state
      return {
        userCosmetics: {
          ...state.userCosmetics,
          [userId]: [...existing, { cosmeticId, unlockedAt: new Date().toISOString() }]
        }
      }
    })
  },

  // Trigger a terrarium-wide effect
  triggerEffect: (effectId, userId, username) => {
    const effect = EFFECT_PRODUCTS.find(e => e.id === effectId)
    if (!effect) return

    const activeEffect = {
      id: `${effectId}_${Date.now()}`,
      effectId,
      userId,
      username,
      startedAt: Date.now(),
      duration: effectId === 'rain' ? 30000 : effectId === 'swarm' ? 30000 : 5000
    }

    set(state => ({
      activeEffects: [...state.activeEffects, activeEffect]
    }))

    // Auto-remove after duration
    setTimeout(() => {
      set(state => ({
        activeEffects: state.activeEffects.filter(e => e.id !== activeEffect.id)
      }))
    }, activeEffect.duration)
  },

  // Add a bits notification
  addBitsNotification: (icon, text, bits) => {
    const id = Date.now()
    set(state => ({
      bitsNotifications: [...state.bitsNotifications, { id, icon, text, bits }]
    }))

    setTimeout(() => {
      set(state => ({
        bitsNotifications: state.bitsNotifications.filter(n => n.id !== id)
      }))
    }, 6000)
  },

  // Clear all effects
  clearEffects: () => set({ activeEffects: [] })
}))

export default useBitsStore
